import {
  Box,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import CommonModal from "../CommonComponents/Modal/CommonModal";
import DynamicTable from "../CommonComponents/Table/DynamicTable";
import LeadDetailBody from "./LeadDetailBody";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LaunchIcon from "@mui/icons-material/Launch";
import { PAGE_SIZE_50 } from "../CommonComponents/Common";
const getRowKey = (row, index) => row.id || row._id || index;

const LeadsTable = ({ leadsObj }) => {
  const history = useHistory();
  const reducer = useSelector((reducer: any) => reducer);
  const {
    ProjectReducer: { leadsList, projectList, leadsCount: totalCount },
  } = reducer;

  const {
    workflows,
    loading,
    setSelectedContactsData,
    selectedRows,
    setSelectedRows,
    setFilterState,
    setLeadsState,
    leadsState,
    searchText,
  } = leadsObj;

  const { pageIndex } = leadsState;
  const [workflowPopup, setWorkflowPopup] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [showSelectedLead, setShowSelectedLead] = useState<any>(null);
  const [leadDetailModal, setLeadDetailModal] = useState(false);
  const [jumpPage, setJumpPage] = useState("");
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (totalCount) {
      setTotalPages(Math.ceil(totalCount / PAGE_SIZE_50));
    }
  }, [pageIndex, searchText, totalCount]);

  const handlePageChange = (event, newPage) => {
    setLeadsState((prev) => ({ ...prev, pageIndex: newPage }));
  };

  useEffect(() => {
    setJumpPage(pageIndex.toString());
  }, [pageIndex]);

  const allSelected = useMemo(
    () => leadsList?.length > 0 && selectedRows?.length === leadsList?.length,
    [selectedRows, leadsList]
  );

  const handleSelectRow = (rowKey: string | number, checked: boolean) => {
    if (checked) {
      setSelectedRows((prev) => [...prev, rowKey]);

      const rowData = leadsList?.find(
        (row, index) => getRowKey(row, index) === rowKey
      );
      if (rowData) {
        setSelectedContactsData((prev) => [...prev, rowData?._id]);
      }
    } else {
      setSelectedRows((prev) => prev.filter((key) => key !== rowKey));

      const rowData = leadsList?.find(
        (row, index) => getRowKey(row, index) === rowKey
      );
      if (rowData) {
        setSelectedContactsData((prev) =>
          prev.filter((contact) => !(contact?._id === (rowData?._id || "")))
        );
      }
    }
    setFilterState({
      firstName: "",
      lastName: "",
      phone: "",
      createdAt: {
        from: "",
        to: "",
      },
      updatedAt: {
        from: "",
        to: "",
      },
    });
  };

  const openLeadDetailModal = (lead) => {
    setShowSelectedLead(lead);
    setLeadDetailModal(true);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allRowKeys = leadsList.map((row, i) => getRowKey(row, i));
      setSelectedRows(allRowKeys);
      const allContactsIds = leadsList.map(({ _id }) => _id);
      setSelectedContactsData(allContactsIds);
    } else {
      setSelectedRows([]);
      setSelectedContactsData([]);
    }
    setFilterState({
      firstName: "",
      lastName: "",
      phone: "",
      tags: [],
      createdAt: {
        from: "",
        to: "",
      },
      updatedAt: {
        from: "",
        to: "",
      },
    });
  };

  const handlePrevious = () => {
    setLeadsState((prev: any) => ({
      ...prev,
      pageIndex: Math.max(prev.pageIndex - 1, 1),
    }));
    setJumpPage("");
    setSelectedRows([]);
    setSelectedContactsData([]);
  };

  const handleNext = () => {
    setLeadsState((prev: any) => ({
      ...prev,
      pageIndex: Math.min(prev.pageIndex + 1, leadsList?.length),
    }));
    setJumpPage("");
    setSelectedRows([]);
    setSelectedContactsData([]);
  };

  const handleJumpPage = () => {
    setLeadsState((prev: any) => ({
      ...prev,
      pageIndex: parseInt(jumpPage) || 1,
    }));
    setSelectedRows([]);
    setSelectedContactsData([]);
  };

  const onJumpPageChange = (e) => {
    if (totalPages >= parseInt(e.target.value) || e.target.value === "") {
      setJumpPage(e.target.value);
    }
  };

  return (
    <>
      <DynamicTable
        columns={[
          {
            label: (
              <Checkbox
                checked={allSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: 20,
                  },
                }}
              />
            ),
            render: (row, index) => {
              const rowKey = getRowKey(row, index);
              return (
                <Checkbox
                  checked={selectedRows.includes(rowKey)}
                  onChange={(e) => handleSelectRow(rowKey, e.target.checked)}
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                />
              );
            },
          },
          {
            label: "First Name",
            field: "firstName",
            sortable: true,
          },
          {
            label: "Last Name",
            field: "lastName",
            sortable: true,
          },
          {
            label: "Workflows",
            render: (rowData) => {
              const workflowNames = rowData?.activeWorkFlowIds
                ?.map((id) => workflows?.find((wf: any) => wf?.id === id)?.name)
                ?.filter(Boolean);
              return (
                <Typography className="font-12-mediumgrey">
                  {workflowNames?.length > 0 ? workflowNames?.join(", ") : "--"}
                </Typography>
              );
            },
          },
          {
            label: "Tags",
            render: (rowData) => {
              const tags = rowData?.tag?.filter(Boolean);
              return (
                <Typography className="font-12-mediumgrey">
                  {tags?.length > 0 ? tags?.join(", ") : "--"}
                </Typography>
              );
            },
          },
          {
            label: "Actions",
            isAction: true,
            render: (row) => (
              <>
                <IconButton
                  disabled={row?.history?.length < 1}
                  title="View Lead History"
                  onClick={() => {
                    setWorkflowPopup(true);
                    setSelectedLead(row);
                  }}
                  sx={{
                    "&.Mui-disabled svg": {
                      opacity: 0.4,
                      color: "inherit",
                    },
                  }}
                >
                  <LaunchIcon fontSize="small" />
                </IconButton>
              </>
            ),
          },
        ]}
        data={leadsList}
        onRowClick={(row) => openLeadDetailModal(row)}
        loading={loading}
        noDataAvailableMessage="No Leads Available"
        handleJumpPageChange={onJumpPageChange}
        jumpPage={jumpPage}
        handleJumpPage={handleJumpPage}
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        handlePageChange={handlePageChange}
        totalRowCount={totalCount}
        totalPages={totalPages}
        currentPage={pageIndex}
        rowCount={leadsList?.length}
        jumpTo={true}
      />

      <CommonModal
        body={
          <Box>
            <List disablePadding>
              {[...new Set(selectedLead?.history?.map((h) => h.workflowId))]
                .map((id) => workflows.find((wf) => wf?.id === id))
                .filter(Boolean)
                .map((workflow) => (
                  <ListItem
                    sx={{
                      border: "1px solid grey",
                      borderRadius: "8px ",
                      my: 1,
                    }}
                    disablePadding
                    key={workflow.id}
                  >
                    <ListItemButton
                      onClick={() =>
                        history.push(
                          `/account/lead-details/${selectedLead?._id}/${workflow.id}`
                        )
                      }
                    >
                      <ListItemText
                        primary={workflow.name}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: 500,
                        }}
                      />
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <ArrowForwardIosIcon />
                      </ListItemIcon>
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          </Box>
        }
        handleSave={false}
        heading={`Select Workflow`}
        show={workflowPopup}
        size={600}
        toggle={() => setWorkflowPopup((prev) => !prev)}
        saveButtonText="Topup"
        spin={false}
        showSaveButton={false}
      />

      {leadDetailModal && (
        <CommonModal
          body={
            <LeadDetailBody
              leadData={showSelectedLead}
              projectList={projectList}
            />
          }
          handleSave={() => {}}
          heading={`Lead Details`}
          show={leadDetailModal}
          size={750}
          toggle={() => {
            setShowSelectedLead(null);
            setLeadDetailModal(false);
          }}
          showSaveButton={false}
          spin={false}
        />
      )}
    </>
  );
};

export default LeadsTable;
